import React from 'react'
import Modal from '../../../components/Modal/Modal'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Button } from '@demandlocal/components'
import { useNavigate } from 'react-router'
import toast from 'react-hot-toast'
import { useCreateDealership } from '../../../api/admin/useCreateDealership'
import DealershipInfo from './Routes/DealershipInfo'
import DealershipIntegrations from './Routes/DealershipIntegrations'
import DealershipPremiumFeatures from './Routes/DealershipPremiumFeatures'
import { queryClient } from '../../../api/queryClient'

export default function AddDealership(): JSX.Element {
  const formContext = useForm({
    mode: 'onSubmit',
    defaultValues: {
      country: { label: 'USA', value: 'USA' },
      enable_conversion_report: true,
    },
  })

  const location = useLocation()
  const path = location?.pathname?.split('/')?.pop() ?? 'info'
  const navigate = useNavigate()

  const { mutateAsync } = useCreateDealership()
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = formContext

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    if (path === 'details') return navigate('integrations')
    if (path === 'integrations') return navigate('premium')

    const toastId = toast.loading('Creating dealership...')
    await mutateAsync({
      data: {
        dealership: values?.dealership,
        address: values?.address,
        city: values?.city,
        state: values?.state?.value,
        country: values?.country?.value,
        zip: values?.zip,
        email: values?.email,
        phone: values?.phone,
        url: values?.url,
        client_id: values?.client_id,
        industry: values?.industry?.value,
        organizations: values?.organizations,
        elead_subscription: values?.elead_subscription,
        vin_solutions_dealership: values?.vin_solutions_dealership,
        cdk_fi_sales_subscription: values?.cdk_fi_sales_subscription,
        dealersocket_dealership_id: values?.dealersocket_dealership_id,
        tekion_dealership_id: values?.tekion_dealership_id,
        procede_dealership_id: values?.procede_dealership_id,
        promax_dealership_id: values?.promax_dealership_id,
        drivecentric_dealership_id: values?.drivecentric_dealership_id,
        amazon_ads_account_id: values?.amazon_ads_account_id,
        enable_audience_download: values?.enable_audience_download ?? false,
        enable_path_to_conversion_report:
          values?.enable_path_to_conversion_report ?? false,
        enable_conversion_report: values?.enable_conversion_report ?? false,
        enable_creative_report: values?.enable_creative_report ?? false,
        enable_path_to_sale_report: values?.enable_path_to_sale_report ?? false,
        enable_polk_report: values?.enable_polk_report ?? false,
        enable_ga4_report: values?.enable_ga4_report ?? false,
        attribution_tracking:
          values?.attribution_goals?.length && values?.attribution_type?.value
            ? {
                type: values?.attribution_type?.value,
                goals: values?.attribution_goals?.map((d) => d.value),
              }
            : undefined,
      },
    })
    await queryClient.invalidateQueries(['admin', 'dealerships'])
    toast.success('Saved!', { id: toastId })
    navigate('../')
  }
  return (
    <Modal modalRoute={true} isOpen={true} key={'AdminUserAdd'}>
      <FormProvider {...formContext}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={'flex flex-1 flex-col h-screen'}>
          <div
            className={
              'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full h-screen'
            }>
            <div className={'text-3xl font-bold mt-12'}>Add new dealership</div>
            <div className={'pt-12 pb-2 border-b-2'} />
            <div className={'font-Spartan overflow-scroll h-full'}>
              <Routes>
                <Route
                  path={'details'}
                  element={<DealershipInfo formContext={formContext} />}
                />
                <Route path={'integrations'} element={<DealershipIntegrations />} />
                <Route path={'premium'} element={<DealershipPremiumFeatures />} />
                <Route path={'*'} element={<Navigate to={'details'} />} />
              </Routes>
            </div>
            <div className={'flex justify-between py-5 border-t-2'}>
              <Button
                disabled={isSubmitting}
                variant={'secondary'}
                type={'button'}
                animate={false}
                onClick={() => {
                  if (path === 'details') navigate('../')
                  else navigate(-1)
                }}>
                {path === 'details' ? 'Cancel' : 'Back'}
              </Button>
              <Button animate={false} disabled={isSubmitting} variant={'primary'}>
                {path === 'details'
                  ? 'Next: Add integrations'
                  : path === 'integrations'
                  ? 'Next: Add Premium Features'
                  : 'Save'}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  )
}
